import React, { useState, useEffect } from 'react';
import { X, CheckCircle, ChevronRight, User, Building2, MapPin, Briefcase } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INTEREST_OPTIONS = [
  { label: "I'm interested in Visiting", value: "Visiting", icon: "🎫", color: "bg-blue-50 border-blue-200 hover:bg-blue-100 text-blue-900" },
  { label: "I'm interested in Exhibiting", value: "Exhibiting", icon: "🏢", color: "bg-emerald-50 border-emerald-200 hover:bg-emerald-100 text-emerald-900" },
  { label: "I'm interested in Sponsorship", value: "Sponsorship", icon: "⭐", color: "bg-amber-50 border-amber-200 hover:bg-amber-100 text-amber-900" },
  { label: "I'm interested in Speaking", value: "Speaking", icon: "🎤", color: "bg-purple-50 border-purple-200 hover:bg-purple-100 text-purple-900" },
  { label: "Government Participation", value: "Government Participation", icon: "🏛️", color: "bg-red-50 border-red-200 hover:bg-red-100 text-red-900" },
  { label: "Business Matching", value: "Business Matching", icon: "🤝", color: "bg-teal-50 border-teal-200 hover:bg-teal-100 text-teal-900" },
  { label: "I'm an Investor", value: "Investor", icon: "💰", color: "bg-yellow-50 border-yellow-200 hover:bg-yellow-100 text-yellow-900" },
  { label: "I'm from the Media", value: "Media", icon: "📰", color: "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-900" },
];

const COUNTRY_OPTIONS = [
  "Indonesia", "Turkey", "Malaysia", "Saudi Arabia", "UAE", "Singapore", "India",
  "Pakistan", "Bangladesh", "United Kingdom", "United States", "Germany", "France",
  "Japan", "South Korea", "Australia", "Canada", "Nigeria", "South Africa", "Brazil", "Other"
];

export const ThankYouOverlay = ({ open, onClose, contactId, profile }) => {
  const [selectedInterest, setSelectedInterest] = useState('');
  const [profileFields, setProfileFields] = useState({ company: '', country: '', job_title: '' });
  const [profileSaved, setProfileSaved] = useState(false);
  const [interestSaved, setInterestSaved] = useState(false);

  useEffect(() => {
    if (open && profile) {
      setSelectedInterest(profile.interest_category || '');
      setProfileFields({
        company: profile.company || '',
        country: profile.country || '',
        job_title: profile.job_title || ''
      });
      setInterestSaved(!!profile.interest_category);
      setProfileSaved(!!(profile.company && profile.country));
    }
  }, [open, profile]);

  const profileCompletion = () => {
    if (!profile) return 0;
    const fields = ['full_name', 'email', 'whatsapp', 'country', 'company', 'job_title', 'industry', 'interest_category'];
    const merged = { ...profile, ...profileFields, interest_category: selectedInterest || profile?.interest_category };
    const filled = fields.filter(f => merged[f]).length;
    return Math.round((filled / fields.length) * 100);
  };

  const handleSelectInterest = async (interest) => {
    setSelectedInterest(interest);
    if (!contactId) return;
    try {
      await fetch(`${BACKEND_URL}/api/contacts/${contactId}/interest`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interest })
      });
      setInterestSaved(true);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Failed to save interest:', e); }
  };

  const handleSaveProfile = async () => {
    if (!contactId) return;
    try {
      await fetch(`${BACKEND_URL}/api/contacts/${contactId}/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileFields)
      });
      setProfileSaved(true);
      toast.success('Profile updated! Thank you.');
    } catch { toast.error('Failed to save. You can update later.'); }
  };

  if (!open) return null;

  const completion = profileCompletion();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="thankyou-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 transition" data-testid="thankyou-close">
          <X size={20} />
        </button>

        {/* Thank you header */}
        <div className="text-center pt-8 pb-4 px-6">
          <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
          <h3 className="text-xl font-bold text-gray-900">Thank you for your interest in</h3>
          <h3 className="text-xl font-bold text-[#0b2e7f]">ASEAN Business Forum 2026</h3>
          
          {/* Profile completion bar */}
          <div className="mt-4 mx-auto max-w-xs">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Profile completion</span>
              <span className="font-semibold text-[#0b2e7f]">{completion}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-[#0b2e7f] to-[#1a4db3] h-2 rounded-full transition-all duration-500" style={{ width: `${completion}%` }} />
            </div>
          </div>
        </div>

        {/* Interest selection */}
        {!interestSaved && (
          <div className="px-6 pb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">What are you most interested in?</h4>
            <div className="grid grid-cols-2 gap-2">
              {INTEREST_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleSelectInterest(opt.value)}
                  className={`flex items-center gap-2 p-3 rounded-lg border text-left text-sm font-medium transition-all ${
                    selectedInterest === opt.value 
                      ? 'ring-2 ring-[#0b2e7f] bg-blue-50 border-blue-300' 
                      : opt.color
                  }`}
                  data-testid={`interest-${opt.value.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <span className="text-lg">{opt.icon}</span>
                  <span className="text-xs leading-tight">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {interestSaved && selectedInterest && (
          <div className="px-6 pb-2">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-sm text-green-800">
              <CheckCircle size={16} />
              <span>Interest recorded: <strong>{selectedInterest}</strong></span>
            </div>
          </div>
        )}

        {/* Optional profile completion */}
        {!profileSaved && (
          <div className="px-6 pb-6">
            <div className="border-t pt-4 mt-2">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <User size={14} /> Help us personalize your experience
                <span className="text-xs font-normal text-gray-400">(Optional)</span>
              </h4>
              <div className="space-y-3">
                {!profile?.company && (
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                    <input
                      type="text" value={profileFields.company}
                      onChange={e => setProfileFields({...profileFields, company: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Company / Organization" data-testid="profile-company-input"
                    />
                  </div>
                )}
                {!profile?.country && (
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                    <select
                      value={profileFields.country}
                      onChange={e => setProfileFields({...profileFields, country: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                      data-testid="profile-country-select"
                    >
                      <option value="">Select Country</option>
                      {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                {!profile?.job_title && (
                  <div className="flex items-center gap-2">
                    <Briefcase size={14} className="text-gray-400 flex-shrink-0" />
                    <input
                      type="text" value={profileFields.job_title}
                      onChange={e => setProfileFields({...profileFields, job_title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Job Title" data-testid="profile-jobtitle-input"
                    />
                  </div>
                )}
                <Button onClick={handleSaveProfile} size="sm" className="w-full bg-[#0b2e7f] hover:bg-[#1a4db3] text-white" data-testid="profile-save-btn">
                  Save & Continue <ChevronRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {profileSaved && (
          <div className="px-6 pb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-sm text-green-800">
              <CheckCircle size={16} />
              <span>Profile updated! Thank you for helping us personalize your experience.</span>
            </div>
          </div>
        )}

        {/* Close button */}
        <div className="px-6 pb-6 text-center">
          <Button onClick={onClose} variant="outline" size="sm" data-testid="thankyou-done-btn">
            Continue Browsing
          </Button>
        </div>
      </div>
    </div>
  );
};
